import { FilterState } from '@/components/filters/index';
import { Button } from '@/components/ui/Button';
import { ListFilter } from '@/components/ui/ListFilter';
import { RangeFilter } from '@/components/ui/RangeFilter';
import {
    BeerStyles,
    Categories,
    PackagingTypes,
    Vendors,
    Volumes,
} from '@/types';
import { X } from 'lucide-react';
import React, { useEffect } from 'react';

interface SidebarFiltersListProps {
    isOpen: boolean;
    onClose: () => void;
    categoriesById: Categories;
    beerStylesById: BeerStyles;
    vendorsById: Vendors;
    packagingTypesById: PackagingTypes;
    volumesById: Volumes;
    filters: FilterState;
    onToggleFilter: (key: keyof FilterState, value: string) => void;
    onClearFilter: (key: keyof FilterState) => void;
    onResetFilters: () => void;
    onSetFilters: React.Dispatch<React.SetStateAction<FilterState>>;
    onApply: () => void;
}

export function SidebarFiltersList({
    isOpen,
    onClose,
    categoriesById,
    beerStylesById,
    vendorsById,
    packagingTypesById,
    volumesById,
    filters,
    onToggleFilter,
    onClearFilter,
    onResetFilters,
    onSetFilters,
    onApply,
}: SidebarFiltersListProps) {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        // Cleanup on unmount or close
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const categories = Object.keys(categoriesById);
    const beerStyles = Object.keys(beerStylesById);
    const vendors = Object.keys(vendorsById);
    const volumes = Object.keys(volumesById);
    const packagingTypes = Object.keys(packagingTypesById);
    return (
        <>
            {isOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
                    onClick={onClose}
                />
            )}

            <aside
                className={`fixed inset-y-0 left-0 z-50 flex w-[90vw] transform flex-col border-r border-zinc-800 bg-zinc-950 transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'} `}
            >
                {/* Header - Fixed */}
                <div className="flex items-center justify-between border-b border-zinc-900 p-5">
                    <h2 className="text-xl font-bold tracking-wide text-white uppercase">
                        Фильтры
                    </h2>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-zinc-500 md:hidden"
                        onClick={onClose}
                    >
                        <X className="h-5 w-5" />
                    </Button>
                </div>

                {/* Scrollable body */}
                <div className="custom-scrollbar flex-1 overflow-x-hidden overflow-y-auto">
                    <div className="space-y-2 px-2 py-2">
                        <ListFilter
                            label="Категория"
                            items={categories}
                            renderLabel={(item) => categoriesById[item].name}
                            selectedItems={filters.category ?? []}
                            onChange={(val) => onToggleFilter('category', val)}
                            getCounts={(val) => categoriesById[val].pCount}
                            onClear={() => onClearFilter('category')}
                        />

                        <ListFilter
                            label="Производитель"
                            items={vendors}
                            renderLabel={(item) => vendorsById[item].name}
                            selectedItems={filters.vendor ?? []}
                            onChange={(val) => onToggleFilter('vendor', val)}
                            getCounts={(val) => vendorsById[val].pCount}
                            scrollable={beerStyles.length > 15}
                            onClear={() => onClearFilter('vendor')}
                        />

                        <ListFilter
                            label="Стиль"
                            items={beerStyles}
                            renderLabel={(item) => beerStylesById[item].name}
                            selectedItems={filters.style ?? []}
                            onChange={(val) => onToggleFilter('style', val)}
                            getCounts={(val) => beerStylesById[val].pCount}
                            scrollable={beerStyles.length > 15}
                            onClear={() => onClearFilter('style')}
                        />

                        <RangeFilter
                            label="Цена, ₽"
                            minValue={filters.minPrice}
                            maxValue={filters.maxPrice}
                            onChange={(type, value) =>
                                onSetFilters((prev) => ({
                                    ...prev,
                                    [type === 'min' ? 'minPrice' : 'maxPrice']:
                                        value,
                                }))
                            }
                            step={10}
                        />

                        <ListFilter
                            label="Тара"
                            items={packagingTypes}
                            renderLabel={(item) =>
                                String(packagingTypesById[item].label)
                            }
                            selectedItems={filters.packagingType ?? []}
                            onChange={(val) =>
                                onToggleFilter('packagingType', val)
                            }
                            scrollable={packagingTypes.length > 15}
                            onClear={() => onClearFilter('packagingType')}
                        />

                        <ListFilter
                            label="Объем, л"
                            items={volumes}
                            renderLabel={(item) =>
                                String(volumesById[item].title)
                            }
                            selectedItems={filters.volume ?? []}
                            onChange={(val) => onToggleFilter('volume', val)}
                            scrollable={volumes.length > 15}
                            onClear={() => onClearFilter('volume')}
                        />

                        <RangeFilter
                            label="Крепость (ABV)"
                            minValue={filters.minAbv}
                            maxValue={filters.maxAbv}
                            onChange={(type, value) =>
                                onSetFilters((prev) => ({
                                    ...prev,
                                    [type === 'min' ? 'minAbv' : 'maxAbv']:
                                        value,
                                }))
                            }
                            step={0.1}
                        />

                        <RangeFilter
                            label="Горечь (IBU)"
                            minValue={filters.minIbu}
                            maxValue={filters.maxIbu}
                            onChange={(type, value) =>
                                onSetFilters((prev) => ({
                                    ...prev,
                                    [type === 'min' ? 'minIbu' : 'maxIbu']:
                                        value,
                                }))
                            }
                            step={1}
                        />
                    </div>
                </div>

                {/* Footer actions - Sticky bottom bar */}
                <div className="border-t border-zinc-800 bg-zinc-950/80 p-5">
                    <div className="flex flex-col gap-3">
                        <Button
                            variant="primary"
                            className="h-11 w-full font-bold tracking-wider uppercase"
                            onClick={() => onApply()}
                        >
                            Применить
                        </Button>
                        <Button
                            variant="ghost"
                            className="h-10 w-full text-xs text-zinc-500 hover:bg-transparent hover:text-red-400"
                            onClick={onResetFilters}
                        >
                            Сбросить все
                        </Button>
                    </div>
                </div>
            </aside>
        </>
    );
}
